import { getConfig, klinikenApi } from "@/api";
import { Trigger } from "@/components/forms/Form/types";
import { Option } from "@/components/forms/inputs/Autocomplete/types";
import { fetchProfilesRollAndOrgUnit } from "@/components/forms/reusables/profileHelper";
import { getVardformFromValue } from "@/components/forms/reusables/vardformHelper";
import { useStore } from "@/store";
import { Avtalstyper } from "@/utils/constants";
import moment from "moment";

const operationsenhetTrigger = () => ({
    fieldName: "operationsenhet",
    dependentOn: "status",
    action: ({ dependentOnValue }: { dependentOnValue: any }) => {
        if (["Bokad", "Pågående", "Genomförd", "Signerad"].includes(dependentOnValue)) {
            return {
                disabled: true,
                suffix: "Du måste avboka operationen om du vill ändra operationsenhet",
            };
        } else {
            return {
                disabled: false,
                suffix: null,
            };
        }
    },
});

const huvudoperatorTrigger = () => ({
    fieldName: "huvudoperator",
    dependentOn: ["operationsenhet", "huvudoperator_ej_bestamd", "status"],
    action: async ({
        value,
        dependentOnValue,
        initial = false,
    }: {
        value: any;
        dependentOnValue: any;
        initial?: boolean;
    }) => {
        const isLocked = ["Bokad", "Pågående", "Genomförd", "Signerad"].includes(dependentOnValue?.["status"]);
        const isEjBestamd = !!dependentOnValue?.["huvudoperator_ej_bestamd"];
        const hasOperationsenhet = !!dependentOnValue?.["operationsenhet"];

        let disabled = false;
        let required = true;
        let suffix: string | null = null;

        if (isLocked) {
            disabled = true;
            required = !isEjBestamd;
            suffix = "Du måste avboka operationen om du vill ändra operatör";
        } else if (isEjBestamd) {
            disabled = true;
            required = false;
            suffix = null;
        } else if (!hasOperationsenhet) {
            disabled = true;
            required = true;
            suffix = null;
        }

        let options: Option[] = [];
        if (hasOperationsenhet) {
            const params = {
                is_active: true,
                operator: true,
                orgUnit: dependentOnValue["operationsenhet"],
            };
            options = await fetchProfilesRollAndOrgUnit({
                params,
                showRoll: "no",
                showOrgUnit: "if-multiple",
            });
        }

        let newValue = value;
        if (isEjBestamd) {
            newValue = null;
        } else if (!initial && hasOperationsenhet && !isLocked && !value) {
            // Sätt den inloggade användaren som huvudoperator om
            // 1. ingen huvudoperator redan är vald
            // 2. den inloggade användaren är kirurg
            // 3. den inloggade användaren tillhör samma operationsenhet
            const store = useStore();
            const currentProfile = store.getters["userData/currentProfile"];
            if (
                currentProfile?.roll?.code === "kirurg" &&
                currentProfile?.orgUnit?.id === dependentOnValue["operationsenhet"]
            ) {
                newValue = {
                    value: currentProfile.pk,
                    label: currentProfile.user_display.name,
                };
            }
        }

        return {
            value: newValue,
            options,
            setOptionsDefault: true,
            disabled,
            required,
            suffix,
        };
    },
});

const assOperatorEjBestamdTrigger = () => ({
    fieldName: "ass_operatorer",
    dependentOn: "assoperator_ej_bestamd",
    action: ({ value, dependentOnValue }: { value: any; dependentOnValue: any }) => {
        const isEjBestamd = !!dependentOnValue;
        return {
            disabled: isEjBestamd,
            value: isEjBestamd ? null : value,
        };
    },
});

const huvudoperatorEjBestamdTrigger = () => ({
    fieldName: "huvudoperator_ej_bestamd",
    dependentOn: "status",
    action: ({ dependentOnValue }: { dependentOnValue: any }) => {
        const lockedStatuses = ["Bokad", "Pågående", "Genomförd", "Signerad"];
        return {
            disabled: lockedStatuses.includes(dependentOnValue),
        };
    },
});

const inlaggningAntalDagarForeTrigger = () => ({
    fieldName: "inlaggning_antal_dagar_fore",
    dependentOn: "planerad_vardform",
    action: ({ value, dependentOnValue }: { value: any; dependentOnValue: any }) => {
        const vardform = getVardformFromValue(dependentOnValue);
        const isSlutenvard = vardform?.typ === "SLUTENVÅRD";
        return {
            disabled: !isSlutenvard,
            required: isSlutenvard,
            value: isSlutenvard ? value : null,
        };
    },
});

const infoTillVardavdelningenTrigger = () => ({
    fieldName: "info_till_vardavdelningen",
    dependentOn: "planerad_vardform",
    action: ({ value, dependentOnValue }: { value: any; dependentOnValue: any }) => {
        const vardform = getVardformFromValue(dependentOnValue);
        const isSlutenvard = vardform?.typ === "SLUTENVÅRD";
        return {
            disabled: !isSlutenvard,
            value: isSlutenvard ? value : "",
        };
    },
});

const preliminartDatumTrigger = () => ({
    fieldName: "preliminart_datum",
    dependentOn: "prio",
    action: ({
        value,
        dependentOnValue,
        selectedOption,
        initial = false,
    }: {
        value: any;
        dependentOnValue: any;
        selectedOption: any;
        initial?: boolean;
    }) => {
        const dagar = dependentOnValue?.dagar ?? selectedOption?.[0]?.dagar;
        return {
            value: dagar && !initial ? moment().add(dagar, "days").format("yyyy-MM-DD HH:mm") : value,
        };
    },
});

const forsakringbolagTrigger = () => ({
    fieldName: "forsakringsbolag",
    dependentOn: "avtal",
    action: ({
        value,
        dependentOnValue,
        selectedOption,
        initial = false,
    }: {
        value: any;
        dependentOnValue: any;
        selectedOption: any;
        initial?: boolean;
    }) => {
        const isForsakringsbolag =
            (dependentOnValue?.typ && dependentOnValue.typ === Avtalstyper.FORSAKRING) ||
            (selectedOption?.length > 0 && selectedOption[0]?.typ && selectedOption[0].typ === Avtalstyper.FORSAKRING);
        return {
            value: isForsakringsbolag || initial ? value : null,
            disabled: !isForsakringsbolag,
            required: isForsakringsbolag,
        };
    },
});

const antalDagarPaAvdelningTrigger = () => ({
    fieldName: "antal_dagar_pa_avdelning",
    dependentOn: "planerad_vardform",
    action: ({ value, dependentOnValue }: { value: any; dependentOnValue: any }) => {
        const vardform = getVardformFromValue(dependentOnValue);
        const isSlutenvard = vardform?.typ === "SLUTENVÅRD";
        return {
            disabled: !isSlutenvard,
            required: isSlutenvard,
            value: isSlutenvard ? value : null,
        };
    },
});

const optidTrigger = () => ({
    fieldName: "optid",
    dependentOn: [
        "huvudoperator",
        "huvudoperator_ej_bestamd",
        "typoperation",
        "huvuddiagnos",
        "bidiagnoser",
        "huvudoperationskod",
        "bioperationskoder",
        "niva_fran_till",
    ],
    action: async ({
        value,
        dependentOnValue,
        selectedOption,
        initial = false,
    }: {
        value: any;
        dependentOnValue: any;
        selectedOption: any;
        initial?: boolean;
    }) => {
        if (initial) {
            return;
        }

        if (
            dependentOnValue.huvudoperator &&
            dependentOnValue.huvuddiagnos &&
            dependentOnValue.huvudoperationskod &&
            !initial
        ) {
            const huvuddiagnos =
                typeof dependentOnValue.huvuddiagnos === "object"
                    ? dependentOnValue.huvuddiagnos.value
                    : dependentOnValue.huvuddiagnos;

            const huvudoperationskod =
                typeof dependentOnValue.huvudoperationskod === "object"
                    ? dependentOnValue.huvudoperationskod.value
                    : dependentOnValue.huvudoperationskod;

            const bidiagnoser = Array.isArray(dependentOnValue.bidiagnoser)
                ? dependentOnValue.bidiagnoser
                      .map((diag: any) => {
                          if (diag.id !== undefined && diag.id !== null) {
                              return typeof diag.id === "object" ? diag.id.value : diag.id;
                          }
                          return undefined;
                      })
                      .filter((id: any) => id !== undefined)
                : [];

            const bioperationskoder = Array.isArray(dependentOnValue.bioperationskoder)
                ? dependentOnValue.bioperationskoder
                      .map((op: any) => {
                          if (op.id !== undefined && op.id !== null) {
                              return typeof op.id === "object" ? op.id.value : op.id;
                          }
                          return undefined;
                      })
                      .filter((id: any) => id !== undefined)
                : [];

            let nivaer = [];
            if (Array.isArray(dependentOnValue.niva_fran_till) && dependentOnValue.niva_fran_till.length > 0) {
                // Ta bort dubbletter baserat på både 'fran' och 'till'
                nivaer = dependentOnValue.niva_fran_till.filter(
                    (item: any, index: number, self: any[]) =>
                        self.findIndex(
                            (i) => Number(i.fran) === Number(item.fran) && Number(i.till) === Number(item.till)
                        ) === index
                );
            }
            const antal_nivaer = nivaer.reduce((sum: number, item: any) => {
                const fran = Number(item.fran);
                const till = Number(item.till);
                if (Number.isFinite(fran) && Number.isFinite(till) && fran > 0 && till > 0 && till >= fran) {
                    return sum + (till - fran);
                }
                return sum;
            }, 0);

            /**
             * Skapa URLSearchParams för att hantera array-parametrar korrekt
             * ofta fick vi problem med att skicka array som query params vid användning av GET anrop, ex:
             * ?bidiagnoser[]=diag1,bidiagnoser[]=diag2 istället för ?bidiagnoser=diag1&bidiagnoser=diag2 etc.
             * med URLSearchParams blir det alltid rätt format.
             */

            const params = new URLSearchParams();
            params.append("huvuddiagnos", huvuddiagnos);
            params.append("huvudoperationskod", huvudoperationskod);
            bidiagnoser.forEach((diag: any) => {
                params.append("bidiagnoser", diag);
            });
            bioperationskoder.forEach((bioperationskoder: any) => {
                params.append("bioperationskoder", bioperationskoder);
            });
            params.append("antal_nivaer", antal_nivaer);

            try {
                const huvud_operator =
                    typeof dependentOnValue.huvudoperator === "object"
                        ? dependentOnValue.huvudoperator.value
                        : dependentOnValue.huvudoperator;
                const response = await klinikenApi.get(
                    `/operation/medianoptid/${huvud_operator}/`,
                    getConfig({
                        params,
                    })
                );

                const medianOptid = response.data.optid_completed__median;
                const store = useStore();
                const typoperation: {
                    [key: string]: any;
                } = store.getters["operation/typoperation/typoperationById"]({
                    id: dependentOnValue.typoperation,
                });

                if (medianOptid) {
                    return {
                        value: medianOptid,
                        suffix: "Baserad på operatörens mediantid",
                    };
                } else if (typoperation?.optid) {
                    return {
                        value: typoperation.optid,
                        suffix: "Baserad på typoperation",
                    };
                } else {
                    return {
                        value: value,
                        suffix: "",
                    };
                }
            } catch (error) {
                console.error("Failed to fetch median optid:", error);
            }
        }
    },
});

const blockadTrigger = () => ({
    fieldName: "blockad",
    dependentOn: "anestesi",
    action: ({
        value,
        dependentOnValue,
        selectedOption,
        initial = false,
    }: {
        value: any;
        dependentOnValue: any;
        selectedOption: any;
        initial?: boolean;
    }) => {
        /* dependentOnValue kan vara ett objekt vid inladdning av data i formuläret men är senare id på
        anestesi, och då tar vi första alternativet i selectedOption. */
        const isBlockad = dependentOnValue?.is_blockad || (selectedOption?.length > 0 && selectedOption[0]?.is_blockad);
        return {
            value: isBlockad || initial ? value : null,
            required: isBlockad,
            disabled: !isBlockad,
        };
    },
});

const rokningTrigger = () => ({
    fieldName: "rokavvanjning",
    dependentOn: "rokare",
    action: ({ value, dependentOnValue }: { value: any; dependentOnValue: any }) => ({
        disabled: dependentOnValue !== "JA",
        required: dependentOnValue === "JA",
        value: dependentOnValue === "JA" ? value : null,
    }),
});

const sprakTrigger = () => ({
    fieldName: "sprak",
    dependentOn: "tolk",
    action: ({ value, dependentOnValue }: { value: any; dependentOnValue: any }) => ({
        disabled: dependentOnValue !== "JA",
        required: dependentOnValue === "JA",
        value: dependentOnValue === "JA" ? value : null,
    }),
});

const diabetesTrigger = () => ({
    fieldName: "diabetes_lakemedelsform",
    dependentOn: "diabetes",
    action: ({ value, dependentOnValue }: { value: any; dependentOnValue: any }) => ({
        disabled: !dependentOnValue,
        required: !!dependentOnValue,
        value: dependentOnValue ? value : null,
    }),
});

const antikoagulantiaTrigger = () => ({
    fieldName: "satts_ut_dagar_innan",
    dependentOn: "antikoagulantia",
    action: ({ value, dependentOnValue }: { value: any; dependentOnValue: any }) => ({
        disabled: dependentOnValue !== "JA",
        required: dependentOnValue === "JA",
        value: dependentOnValue === "JA" ? value : null,
    }),
});

const antalDagarTrigger = () => ({
    fieldName: "antal_dagar_pa_avdelning",
    dependentOn: ["inlaggning_antal_dagar_fore", "antal_dagar_pa_avdelning"],
    action: ({
        value,
        dependentOnValue,
        initial = false,
    }: {
        value: any;
        dependentOnValue: any;
        initial?: boolean;
    }) => {
        const inlaggning_antal_dagar_fore = dependentOnValue.inlaggning_antal_dagar_fore;
        const antal_dagar_pa_avdelning = dependentOnValue.antal_dagar_pa_avdelning;
        let suffix = "";
        const antal_dagar_avdelning = antal_dagar_pa_avdelning == null ? null : Number(antal_dagar_pa_avdelning);
        const inlaggning_dagar_fore = inlaggning_antal_dagar_fore == null ? null : Number(inlaggning_antal_dagar_fore);

        if (!initial && (antal_dagar_avdelning == null || antal_dagar_avdelning < 1)) {
            suffix = "Antal dagar på avdelning måste vara minst 1.";
        } else if (
            inlaggning_dagar_fore != null &&
            antal_dagar_avdelning != null &&
            antal_dagar_avdelning < inlaggning_dagar_fore
        ) {
            suffix = "Total antal dygn på avdelningen kan inte vara mindre än inlaggning antal dagar före.";
        }

        return {
            value: value < 1 ? null : value,
            suffix: suffix,
        };
    },
});

export const formTriggers = [
    operationsenhetTrigger(),
    huvudoperatorTrigger(),
    huvudoperatorEjBestamdTrigger(),
    inlaggningAntalDagarForeTrigger(),
    infoTillVardavdelningenTrigger(),
    preliminartDatumTrigger(),
    forsakringbolagTrigger(),
    optidTrigger(),
    blockadTrigger(),
    rokningTrigger(),
    sprakTrigger(),
    diabetesTrigger(),
    antikoagulantiaTrigger(),
    antalDagarPaAvdelningTrigger(),
    antalDagarTrigger(),
    assOperatorEjBestamdTrigger(),
] as Array<Trigger>;
